import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Briefcase,
  Users,
  ExternalLink,
  Search,
  MapPin,
  Clock,
  Award,
  Layers,
  CheckCircle,
  Copy,
  Check,
  Eye,
  RefreshCw,
  Building,
  TrendingUp,
  Plus,
  Pencil,
  Trash2,
  X,
  Sparkles,
  Save,
  Tag,
} from 'lucide-react';
import { AdminChrome } from '../components/admin/AdminChrome';
import { Sheet, SheetContent, SheetTitle } from '../components/ui/sheet';
import { adminApi, useNoIndex } from '../components/admin/adminApi';

const DEFAULT_BLANK_OPPORTUNITY = {
  title: '',
  id: '',
  department: 'Business Analyst',
  location: 'Canada, USA, UAE',
  workplaceType: 'Hybrid',
  type: 'Full Time',
  experience: '3-5 years',
  status: 'active',
  summary: '',
  overview: '',
  responsibilities: [''],
  requirements: [''],
  niceToHave: [''],
  benefits: [''],
};

export default function AdminOpportunities() {
  const navigate = useNavigate();
  useNoIndex();

  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Search & Filters
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Preview Drawer
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Form Drawer (Create / Edit)
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_BLANK_OPPORTUNITY);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Load Opportunities from database
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [oppsRes, statsRes] = await Promise.all([
        adminApi.listOpportunities(),
        adminApi.getCareerStats().catch(() => null),
      ]);

      setOpportunities(oppsRes.opportunities || []);
      if (statsRes) setStats(statsRes);
    } catch (err) {
      toast.error('Failed to load opportunities from database.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Unique departments for filter
  const departments = useMemo(() => {
    const set = new Set(opportunities.map((o) => o.department).filter(Boolean));
    return Array.from(set);
  }, [opportunities]);

  // Filtered list
  const filteredOpenings = useMemo(() => {
    return opportunities.filter((job) => {
      const matchesSearch =
        !search.trim() ||
        (job.title && job.title.toLowerCase().includes(search.toLowerCase())) ||
        (job.department && job.department.toLowerCase().includes(search.toLowerCase())) ||
        (job.location && job.location.toLowerCase().includes(search.toLowerCase())) ||
        (job.experience && job.experience.toLowerCase().includes(search.toLowerCase()));

      const matchesDept = deptFilter === 'all' || job.department === deptFilter;
      const matchesStatus = statusFilter === 'all' || (job.status || 'active') === statusFilter;

      return matchesSearch && matchesDept && matchesStatus;
    });
  }, [opportunities, search, deptFilter, statusFilter]);

  // Total applicants across all roles
  const totalApplicants = stats?.total ?? 0;
  const activeCount = useMemo(
    () => opportunities.filter((o) => (o.status || 'active') === 'active').length,
    [opportunities]
  );

  // Copy job link
  const handleCopyLink = (jobId) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${origin}/careers/${jobId}`;
    navigator.clipboard?.writeText(url);
    setCopiedId(jobId);
    toast.success('Opportunity link copied to clipboard.');
    setTimeout(() => setCopiedId(null), 1800);
  };

  // Open Form to Create
  const handleOpenCreateForm = () => {
    setIsEditing(false);
    setFormData(DEFAULT_BLANK_OPPORTUNITY);
    setFormErrors({});
    setIsFormOpen(true);
  };

  // Open Form to Edit
  const handleOpenEditForm = (opp) => {
    setIsEditing(true);
    setFormData({
      ...DEFAULT_BLANK_OPPORTUNITY,
      ...opp,
      responsibilities: opp.responsibilities?.length ? opp.responsibilities : [''],
      requirements: opp.requirements?.length ? opp.requirements : [''],
      niceToHave: opp.niceToHave?.length ? opp.niceToHave : [''],
      benefits: opp.benefits?.length ? opp.benefits : [''],
    });
    setFormErrors({});
    setIsFormOpen(true);
  };

  // Form Array Field Helpers
  const handleArrayItemChange = (field, index, value) => {
    setFormData((prev) => {
      const list = [...(prev[field] || [])];
      list[index] = value;
      return { ...prev, [field]: list };
    });
  };

  const handleAddArrayItem = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...(prev[field] || []), ''],
    }));
  };

  const handleRemoveArrayItem = (field, index) => {
    setFormData((prev) => {
      const list = [...(prev[field] || [])];
      list.splice(index, 1);
      return { ...prev, [field]: list.length ? list : [''] };
    });
  };

  // Auto-generate slug from title
  const handleTitleChange = (val) => {
    setFormData((prev) => {
      const next = { ...prev, title: val };
      if (!isEditing && (!prev.id || prev.id === prev.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))) {
        next.id = val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      }
      return next;
    });
  };

  // Submit Form (Save to DB)
  const handleSubmitOpportunity = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!formData.title.trim()) errors.title = 'Job title is required';
    if (!formData.department.trim()) errors.department = 'Department is required';
    if (!formData.location.trim()) errors.location = 'Location is required';
    if (!formData.summary.trim()) errors.summary = 'A summary is required';

    const cleanResponsibilities = (formData.responsibilities || []).map((s) => s.trim()).filter(Boolean);
    const cleanRequirements = (formData.requirements || []).map((s) => s.trim()).filter(Boolean);
    const cleanNiceToHave = (formData.niceToHave || []).map((s) => s.trim()).filter(Boolean);
    const cleanBenefits = (formData.benefits || []).map((s) => s.trim()).filter(Boolean);

    if (cleanResponsibilities.length === 0) {
      errors.responsibilities = 'Add at least one key responsibility';
    }
    if (cleanRequirements.length === 0) {
      errors.requirements = 'Add at least one core requirement';
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the errors in the form.');
      return;
    }

    setSaving(true);
    const payload = {
      ...formData,
      title: formData.title.trim(),
      department: formData.department.trim(),
      location: formData.location.trim(),
      summary: formData.summary.trim(),
      overview: formData.overview.trim() || formData.summary.trim(),
      responsibilities: cleanResponsibilities,
      requirements: cleanRequirements,
      niceToHave: cleanNiceToHave,
      benefits: cleanBenefits,
    };

    try {
      if (isEditing && formData.id) {
        await adminApi.updateOpportunity(formData.id, payload);
        toast.success(`Opportunity "${payload.title}" updated successfully.`);
      } else {
        await adminApi.createOpportunity(payload);
        toast.success(`Opportunity "${payload.title}" created and saved to database!`);
      }
      setIsFormOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to save opportunity.');
    } finally {
      setSaving(false);
    }
  };

  // Delete Opportunity
  const handleDeleteOpportunity = async (oppId) => {
    try {
      await adminApi.deleteOpportunity(oppId);
      toast.success('Opportunity deleted from database.');
      setDeleteConfirmId(null);
      if (selectedOpp?.id === oppId) setSelectedOpp(null);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete opportunity.');
    }
  };

  return (
    <AdminChrome
      title="Career Opportunities"
      subtitle={`${opportunities.length} role${opportunities.length === 1 ? '' : 's'} stored in database`}
      testId="admin-opportunities-page"
      actions={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={handleOpenCreateForm}
            data-testid="add-opportunity-btn"
          >
            <Plus size={15} /> Add New Opportunity
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-ghost"
            onClick={loadData}
            title="Refresh opportunities list"
            data-testid="refresh-opportunities-btn"
          >
            <RefreshCw size={15} />
          </button>
        </div>
      }
    >
      {/* 1. Summary Cards */}
      <div className="admin-cards">
        <div className="admin-card">
          <span>
            <Briefcase size={14} /> Total in Database
          </span>
          <strong>{opportunities.length}</strong>
          <small>{activeCount} active job postings</small>
        </div>

        <div className="admin-card">
          <span>
            <Users size={14} /> Total Applicants
          </span>
          <strong>{totalApplicants}</strong>
          <small>Candidates applied across all roles</small>
        </div>

        <div className="admin-card">
          <span>
            <Building size={14} /> Departments
          </span>
          <strong>{departments.length}</strong>
          <small>Business Analyst, Sales, AI, Data</small>
        </div>

        <div className="admin-card">
          <span>
            <TrendingUp size={14} /> Hiring Status
          </span>
          <strong style={{ color: activeCount > 0 ? '#86e2b0' : '#ff9b9b' }}>
            {activeCount > 0 ? 'Active' : 'Paused'}
          </strong>
          <small>{activeCount > 0 ? 'Accepting applications' : 'No active openings'}</small>
        </div>
      </div>

      {/* 2. Search & Filter Toolbar */}
      <div className="admin-toolbar" data-testid="opportunities-toolbar">
        <div className="admin-search">
          <Search size={16} />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search role by title, department, location, experience…"
            data-testid="search-opportunities-input"
          />
        </div>

        {/* Department Filter */}
        <select
          className="admin-control"
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          data-testid="filter-dept-select"
        >
          <option value="all">All Departments ({departments.length})</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>

        {/* Status Filter */}
        <select
          className="admin-control"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="filter-status-select"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="closed">Closed</option>
        </select>

        {(search || deptFilter !== 'all' || statusFilter !== 'all') && (
          <button
            type="button"
            className="admin-clear"
            onClick={() => {
              setSearch('');
              setDeptFilter('all');
              setStatusFilter('all');
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* 3. Opportunities Grid */}
      <div className="opportunities-grid" data-testid="opportunities-grid">
        {filteredOpenings.map((job) => {
          const applicantCount = stats?.by_job?.[job.id]?.count ?? 0;
          const statusVal = job.status || 'active';

          return (
            <div
              key={job.id}
              className="opportunity-card"
              data-testid={`opportunity-card-${job.id}`}
            >
              {/* Card Header */}
              <div className="opp-card-top">
                <div className="opp-card-title-group">
                  <span className="opp-dept-pill">{job.department}</span>
                  <h3>{job.title}</h3>
                </div>
                <span
                  className={
                    statusVal === 'active'
                      ? 'opp-badge-active'
                      : statusVal === 'closed'
                      ? 'status-pill status-lost'
                      : 'status-pill status-proposal'
                  }
                  style={{ minHeight: '26px' }}
                >
                  {statusVal === 'active' ? 'Active' : statusVal === 'closed' ? 'Closed' : 'Draft'}
                </span>
              </div>

              {/* Meta details list */}
              <div className="opp-meta-list">
                <span className="opp-meta-item">
                  <MapPin size={13} color="#8ea3c4" />
                  {job.location} ({job.workplaceType || 'Hybrid'})
                </span>
                <span className="opp-meta-item">
                  <Clock size={13} color="#8ea3c4" />
                  {job.type}
                </span>
                <span className="opp-meta-item">
                  <Award size={13} color="#8ea3c4" />
                  {job.experience}
                </span>
              </div>

              {/* Description Summary */}
              <p
                style={{
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: '#a3b1c6',
                  margin: 0,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {job.summary || job.overview}
              </p>

              {/* Applicant Count Highlight Box */}
              <div className="opp-card-applicant-stat">
                <div className="opp-applicant-count">
                  <strong>{applicantCount}</strong>
                  <span>candidate{applicantCount === 1 ? '' : 's'} applied</span>
                </div>
                <button
                  type="button"
                  className="admin-file-btn"
                  onClick={() => navigate(`/admin/careers?job_id=${job.id}`)}
                  title="Filter Career table to candidates for this role"
                  data-testid={`view-applicants-btn-${job.id}`}
                >
                  <Users size={13} /> View Candidates
                </button>
              </div>

              {/* Card Footer Actions */}
              <div className="opp-card-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-ghost"
                  style={{ minHeight: '36px', padding: '0 12px', fontSize: '12.5px' }}
                  onClick={() => handleOpenEditForm(job)}
                  title="Edit opportunity details in database"
                  data-testid={`edit-opp-${job.id}`}
                >
                  <Pencil size={13} /> Edit
                </button>

                <button
                  type="button"
                  className="admin-btn admin-btn-ghost"
                  style={{ minHeight: '36px', padding: '0 12px', fontSize: '12.5px' }}
                  onClick={() => setSelectedOpp(job)}
                  title="View full opportunity specifications"
                  data-testid={`view-opp-specs-${job.id}`}
                >
                  <Eye size={13} /> View
                </button>

                <a
                  href={`/careers/${job.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="admin-btn admin-btn-ghost"
                  style={{ minHeight: '36px', padding: '0 12px', fontSize: '12.5px' }}
                  title="Open live career application page"
                  data-testid={`live-job-link-${job.id}`}
                >
                  <ExternalLink size={13} /> Public
                </a>

                {/* Delete Opportunity */}
                {deleteConfirmId === job.id ? (
                  <span style={{ display: 'inline-flex', gap: '4px', marginLeft: 'auto' }}>
                    <button
                      type="button"
                      className="admin-btn admin-btn-danger"
                      style={{ minHeight: '36px', padding: '0 10px', fontSize: '12px' }}
                      onClick={() => handleDeleteOpportunity(job.id)}
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn-ghost"
                      style={{ minHeight: '36px', padding: '0 8px', fontSize: '12px' }}
                      onClick={() => setDeleteConfirmId(null)}
                    >
                      <X size={13} />
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="admin-row-menu"
                    style={{ color: '#ff9494', marginLeft: 'auto' }}
                    onClick={() => setDeleteConfirmId(job.id)}
                    title="Delete opportunity from database"
                    data-testid={`delete-opp-${job.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}

                <button
                  type="button"
                  className="admin-row-menu"
                  onClick={() => handleCopyLink(job.id)}
                  title="Copy public link to clipboard"
                  data-testid={`copy-job-link-${job.id}`}
                >
                  {copiedId === job.id ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredOpenings.length === 0 && !loading && (
        <div className="admin-empty-state">
          <h3>No opportunities found</h3>
          <p>Click "Add New Opportunity" above to create your first opening in the database.</p>
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            style={{ marginTop: '14px' }}
            onClick={handleOpenCreateForm}
          >
            <Plus size={15} /> Add New Opportunity
          </button>
        </div>
      )}

      {/* 4. Opportunity Specification Preview Drawer */}
      <Sheet
        open={Boolean(selectedOpp)}
        onOpenChange={(open) => {
          if (!open) setSelectedOpp(null);
        }}
      >
        <SheetContent side="right" className="admin-drawer" data-testid="opportunity-spec-drawer">
          {selectedOpp && (
            <>
              <div className="admin-drawer-head">
                <SheetTitle asChild>
                  <h2>{selectedOpp.title}</h2>
                </SheetTitle>
                <p>
                  {selectedOpp.department} • {selectedOpp.location} • {selectedOpp.type}
                </p>

                <div className="admin-drawer-actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn-primary"
                    onClick={() => {
                      const id = selectedOpp.id;
                      setSelectedOpp(null);
                      navigate(`/admin/careers?job_id=${id}`);
                    }}
                  >
                    <Users size={14} /> View Candidates (
                    {stats?.by_job?.[selectedOpp.id]?.count ?? 0})
                  </button>

                  <button
                    type="button"
                    className="admin-btn admin-btn-ghost"
                    onClick={() => {
                      const opp = selectedOpp;
                      setSelectedOpp(null);
                      handleOpenEditForm(opp);
                    }}
                  >
                    <Pencil size={14} /> Edit Opportunity
                  </button>

                  <a
                    href={`/careers/${selectedOpp.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="admin-btn admin-btn-ghost"
                  >
                    <ExternalLink size={14} /> Public Post
                  </a>
                </div>
              </div>

              <div className="admin-drawer-body">
                {/* Role Overview */}
                <div className="detail-section">
                  <div className="detail-section-title">
                    <Layers size={13} /> Role Overview
                  </div>
                  <p style={{ fontSize: '13.5px', lineHeight: '1.7', color: '#c7d3e6' }}>
                    {selectedOpp.overview || selectedOpp.summary}
                  </p>
                </div>

                {/* Key Responsibilities */}
                {selectedOpp.responsibilities && selectedOpp.responsibilities.length > 0 && (
                  <div className="detail-section">
                    <div className="detail-section-title">
                      <CheckCircle size={13} /> Key Responsibilities
                    </div>
                    <ul style={{ paddingLeft: '20px', fontSize: '13.5px', lineHeight: '1.7', color: '#cbd5e1' }}>
                      {selectedOpp.responsibilities.map((r, i) => (
                        <li key={i} style={{ marginBottom: '6px' }}>
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Core Requirements */}
                {selectedOpp.requirements && selectedOpp.requirements.length > 0 && (
                  <div className="detail-section">
                    <div className="detail-section-title">
                      <Award size={13} /> Core Requirements
                    </div>
                    <ul style={{ paddingLeft: '20px', fontSize: '13.5px', lineHeight: '1.7', color: '#cbd5e1' }}>
                      {selectedOpp.requirements.map((req, i) => (
                        <li key={i} style={{ marginBottom: '6px' }}>
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Preferred Qualities */}
                {selectedOpp.niceToHave && selectedOpp.niceToHave.length > 0 && (
                  <div className="detail-section">
                    <div className="detail-section-title">
                      <CheckCircle size={13} /> Preferred Qualities / Nice-to-Have
                    </div>
                    <ul style={{ paddingLeft: '20px', fontSize: '13.5px', lineHeight: '1.7', color: '#cbd5e1' }}>
                      {selectedOpp.niceToHave.map((n, i) => (
                        <li key={i} style={{ marginBottom: '6px' }}>
                          {n}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Benefits */}
                {selectedOpp.benefits && selectedOpp.benefits.length > 0 && (
                  <div className="detail-section">
                    <div className="detail-section-title">
                      <Award size={13} /> Benefits & Perks
                    </div>
                    <ul style={{ paddingLeft: '20px', fontSize: '13.5px', lineHeight: '1.7', color: '#cbd5e1' }}>
                      {selectedOpp.benefits.map((b, i) => (
                        <li key={i} style={{ marginBottom: '6px' }}>
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* 5. ADD / EDIT OPPORTUNITY FORM MODAL / DRAWER */}
      <Sheet
        open={isFormOpen}
        onOpenChange={(open) => {
          if (!open) setIsFormOpen(false);
        }}
      >
        <SheetContent side="right" className="admin-drawer" data-testid="opportunity-form-drawer" style={{ width: '720px', maxWidth: '100vw' }}>
          <form onSubmit={handleSubmitOpportunity}>
            <div className="admin-drawer-head">
              <SheetTitle asChild>
                <h2>{isEditing ? 'Edit Opportunity' : 'Add New Career Opportunity'}</h2>
              </SheetTitle>
              <p>
                {isEditing
                  ? 'Update the specifications and requirements for this role in the database.'
                  : 'Fill in all the role details. Once saved, this opening will be stored in the database and displayed on the Careers page.'}
              </p>

              <div className="admin-drawer-actions">
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={saving}
                  data-testid="save-opportunity-btn"
                >
                  <Save size={14} /> {saving ? 'Saving to Database…' : isEditing ? 'Save Changes' : 'Create & Publish'}
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-ghost"
                  onClick={() => setIsFormOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>

            <div className="admin-drawer-body">
              {/* Basic Info */}
              <div className="detail-section">
                <div className="detail-section-title">
                  <Briefcase size={13} /> Role Details
                </div>

                <div className="cms-form">
                  {/* Job Title */}
                  <div className="cms-field span-full">
                    <label className="cms-label" htmlFor="opp-title">
                      Job Title *
                    </label>
                    <input
                      id="opp-title"
                      className="cms-input"
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      placeholder="e.g. Jr. Business Analyst, Senior AI Engineer"
                      required
                      data-testid="input-opp-title"
                    />
                    {formErrors.title && <span style={{ color: '#ff9b9b', fontSize: '11.5px' }}>{formErrors.title}</span>}
                  </div>

                  {/* Slug / ID */}
                  <div className="cms-field">
                    <label className="cms-label" htmlFor="opp-slug">
                      URL Slug / Identifier *
                    </label>
                    <input
                      id="opp-slug"
                      className="cms-input"
                      type="text"
                      value={formData.id}
                      onChange={(e) => setFormData((prev) => ({ ...prev, id: e.target.value }))}
                      placeholder="e.g. jr-business-analyst"
                      disabled={isEditing}
                      data-testid="input-opp-slug"
                    />
                    <span className="cms-hint">Used in URLs (/careers/:id)</span>
                  </div>

                  {/* Department */}
                  <div className="cms-field">
                    <label className="cms-label" htmlFor="opp-department">
                      Department *
                    </label>
                    <input
                      id="opp-department"
                      className="cms-input"
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData((prev) => ({ ...prev, department: e.target.value }))}
                      placeholder="e.g. Business Analyst, Software Engineering, AI"
                      required
                      data-testid="input-opp-department"
                    />
                    {formErrors.department && <span style={{ color: '#ff9b9b', fontSize: '11.5px' }}>{formErrors.department}</span>}
                  </div>

                  {/* Location */}
                  <div className="cms-field">
                    <label className="cms-label" htmlFor="opp-location">
                      Location *
                    </label>
                    <input
                      id="opp-location"
                      className="cms-input"
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g. Canada, USA, UAE or Toronto, ON"
                      required
                      data-testid="input-opp-location"
                    />
                    {formErrors.location && <span style={{ color: '#ff9b9b', fontSize: '11.5px' }}>{formErrors.location}</span>}
                  </div>

                  {/* Workplace Type */}
                  <div className="cms-field">
                    <label className="cms-label" htmlFor="opp-workplace">
                      Workplace Type
                    </label>
                    <select
                      id="opp-workplace"
                      className="cms-input"
                      value={formData.workplaceType}
                      onChange={(e) => setFormData((prev) => ({ ...prev, workplaceType: e.target.value }))}
                      data-testid="select-opp-workplace"
                    >
                      <option value="Hybrid">Hybrid</option>
                      <option value="Remote">Remote</option>
                      <option value="On-site">On-site</option>
                    </select>
                  </div>

                  {/* Employment Type */}
                  <div className="cms-field">
                    <label className="cms-label" htmlFor="opp-type">
                      Employment Type
                    </label>
                    <select
                      id="opp-type"
                      className="cms-input"
                      value={formData.type}
                      onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value }))}
                      data-testid="select-opp-type"
                    >
                      <option value="Full Time">Full Time</option>
                      <option value="Part Time">Part Time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                    </select>
                  </div>

                  {/* Experience */}
                  <div className="cms-field">
                    <label className="cms-label" htmlFor="opp-experience">
                      Experience Required
                    </label>
                    <input
                      id="opp-experience"
                      className="cms-input"
                      type="text"
                      value={formData.experience}
                      onChange={(e) => setFormData((prev) => ({ ...prev, experience: e.target.value }))}
                      placeholder="e.g. Fresher / Entry Level, 3-5 years"
                      data-testid="input-opp-experience"
                    />
                  </div>

                  {/* Status */}
                  <div className="cms-field">
                    <label className="cms-label" htmlFor="opp-status">
                      Status
                    </label>
                    <select
                      id="opp-status"
                      className="cms-input"
                      value={formData.status}
                      onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
                      data-testid="select-opp-status"
                    >
                      <option value="active">Active (Visible on Careers page)</option>
                      <option value="draft">Draft (Hidden)</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Descriptions */}
              <div className="detail-section">
                <div className="detail-section-title">
                  <Layers size={13} /> Role Descriptions
                </div>

                <div className="cms-field" style={{ marginBottom: '16px' }}>
                  <label className="cms-label" htmlFor="opp-summary">
                    Short Summary * <small>(Displayed on opportunity cards)</small>
                  </label>
                  <textarea
                    id="opp-summary"
                    className="cms-input cms-textarea"
                    rows={3}
                    value={formData.summary}
                    onChange={(e) => setFormData((prev) => ({ ...prev, summary: e.target.value }))}
                    placeholder="Brief 1-2 sentence overview of the role..."
                    required
                    data-testid="input-opp-summary"
                  />
                  {formErrors.summary && <span style={{ color: '#ff9b9b', fontSize: '11.5px' }}>{formErrors.summary}</span>}
                </div>

                <div className="cms-field">
                  <label className="cms-label" htmlFor="opp-overview">
                    Full Overview / Company Narrative
                  </label>
                  <textarea
                    id="opp-overview"
                    className="cms-input cms-textarea"
                    rows={4}
                    value={formData.overview}
                    onChange={(e) => setFormData((prev) => ({ ...prev, overview: e.target.value }))}
                    placeholder="Comprehensive description of the position, team mission, and expectations..."
                    data-testid="input-opp-overview"
                  />
                </div>
              </div>

              {/* Key Responsibilities */}
              <div className="detail-section">
                <div className="detail-section-title" style={{ justifyContent: 'space-between' }}>
                  <span>
                    <CheckCircle size={13} /> Key Responsibilities *
                  </span>
                  <button
                    type="button"
                    className="admin-file-btn"
                    onClick={() => handleAddArrayItem('responsibilities')}
                  >
                    <Plus size={12} /> Add Item
                  </button>
                </div>
                {formErrors.responsibilities && (
                  <span style={{ color: '#ff9b9b', fontSize: '11.5px', display: 'block', marginBottom: '8px' }}>
                    {formErrors.responsibilities}
                  </span>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {formData.responsibilities.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        className="cms-input"
                        type="text"
                        value={item}
                        onChange={(e) => handleArrayItemChange('responsibilities', idx, e.target.value)}
                        placeholder={`Responsibility #${idx + 1}...`}
                      />
                      <button
                        type="button"
                        className="admin-row-menu"
                        style={{ color: '#ff9494' }}
                        onClick={() => handleRemoveArrayItem('responsibilities', idx)}
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Core Requirements */}
              <div className="detail-section">
                <div className="detail-section-title" style={{ justifyContent: 'space-between' }}>
                  <span>
                    <Award size={13} /> Core Requirements & Skills *
                  </span>
                  <button
                    type="button"
                    className="admin-file-btn"
                    onClick={() => handleAddArrayItem('requirements')}
                  >
                    <Plus size={12} /> Add Item
                  </button>
                </div>
                {formErrors.requirements && (
                  <span style={{ color: '#ff9b9b', fontSize: '11.5px', display: 'block', marginBottom: '8px' }}>
                    {formErrors.requirements}
                  </span>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {formData.requirements.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        className="cms-input"
                        type="text"
                        value={item}
                        onChange={(e) => handleArrayItemChange('requirements', idx, e.target.value)}
                        placeholder={`Requirement #${idx + 1}...`}
                      />
                      <button
                        type="button"
                        className="admin-row-menu"
                        style={{ color: '#ff9494' }}
                        onClick={() => handleRemoveArrayItem('requirements', idx)}
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Nice to Have */}
              <div className="detail-section">
                <div className="detail-section-title" style={{ justifyContent: 'space-between' }}>
                  <span>
                    <CheckCircle size={13} /> Preferred Qualities / Nice-to-Have
                  </span>
                  <button
                    type="button"
                    className="admin-file-btn"
                    onClick={() => handleAddArrayItem('niceToHave')}
                  >
                    <Plus size={12} /> Add Item
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {formData.niceToHave.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        className="cms-input"
                        type="text"
                        value={item}
                        onChange={(e) => handleArrayItemChange('niceToHave', idx, e.target.value)}
                        placeholder={`Preferred quality #${idx + 1}...`}
                      />
                      <button
                        type="button"
                        className="admin-row-menu"
                        style={{ color: '#ff9494' }}
                        onClick={() => handleRemoveArrayItem('niceToHave', idx)}
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="detail-section">
                <div className="detail-section-title" style={{ justifyContent: 'space-between' }}>
                  <span>
                    <Award size={13} /> What We Offer / Benefits & Perks
                  </span>
                  <button
                    type="button"
                    className="admin-file-btn"
                    onClick={() => handleAddArrayItem('benefits')}
                  >
                    <Plus size={12} /> Add Item
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {formData.benefits.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        className="cms-input"
                        type="text"
                        value={item}
                        onChange={(e) => handleArrayItemChange('benefits', idx, e.target.value)}
                        placeholder={`Benefit #${idx + 1}...`}
                      />
                      <button
                        type="button"
                        className="admin-row-menu"
                        style={{ color: '#ff9494' }}
                        onClick={() => handleRemoveArrayItem('benefits', idx)}
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit footer */}
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  className="admin-btn admin-btn-ghost"
                  onClick={() => setIsFormOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={saving}
                >
                  <Save size={14} /> {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Opportunity'}
                </button>
              </div>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </AdminChrome>
  );
}
